/**
 * Security Settings Management Component
 * ISO 27001 A.9.4.2 - Secure log-on procedures
 * ISO 27001 A.9.4.3 - Password management system
 */

import React, { useState, useEffect } from 'react';
import {
  Shield,
  Clock,
  Lock,
  Key,
  Users,
  AlertTriangle,
  RefreshCw,
  Save,
  Info,
  Smartphone,
} from 'lucide-react';
import { IpWhitelistSettings } from '@/features/auth/components/IpWhitelistSettings';
import { Button, Spinner } from '@/components/ui-v2';
import { notifications } from '@/lib/notifications';
import { confirm } from '@/stores/confirmStore';
import {
  useSecuritySettings,
  useUpdateSecuritySettings,
  useResetSecuritySettings,
  useSecuritySettingsSummary,
} from '../hooks/useSecuritySettings';
import { SecuritySettingsUpdate } from '../api/security-settings.api';

import { useTranslation } from 'react-i18next';
export const SecuritySettings: React.FC = () => {
  const { t } = useTranslation();

  const { data: settings, isLoading, error } = useSecuritySettings();
  const { data: summary } = useSecuritySettingsSummary();
  const updateMutation = useUpdateSecuritySettings();
  const resetMutation = useResetSecuritySettings();

  const [formData, setFormData] = useState<SecuritySettingsUpdate>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Sync form data with fetched settings
  useEffect(() => {
    if (settings) {
      setFormData({
        sessionIdleTimeoutMins: settings.sessionIdleTimeoutMins,
        sessionMaxLifetimeHours: settings.sessionMaxLifetimeHours,
        rememberMeTimeoutDays: settings.rememberMeTimeoutDays,
        maxConcurrentSessions: settings.maxConcurrentSessions,
        forceLogoutOnIpChange: settings.forceLogoutOnIpChange,
        requireReauthForSensitive: settings.requireReauthForSensitive,
        passwordHistoryCount: settings.passwordHistoryCount,
        passwordMaxAgeDays: settings.passwordMaxAgeDays,
        passwordMinLength: settings.passwordMinLength,
        passwordRequireComplex: settings.passwordRequireComplex,
        accountLockoutAttempts: settings.accountLockoutAttempts,
        accountLockoutDuration: settings.accountLockoutDuration,
        inactivityWarningDays: settings.inactivityWarningDays,
        inactivityRevocationDays: settings.inactivityRevocationDays,
        require2FA: settings.require2FA,
        require2FAForAdmins: settings.require2FAForAdmins,
      });
      setHasChanges(false);
    }
  }, [settings]);

  const handleChange = (key: keyof SecuritySettingsUpdate, value: number | boolean) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync(formData);
      notifications.saved(t('admin.security.title'));
      setHasChanges(false);
    } catch (err: any) {
      notifications.saveError(t('admin.security.title'), err.message);
    }
  };

  const handleReset = async () => {
    const confirmed = await confirm({
      title: t('admin.security.resetConfirmTitle'),
      message: t('admin.security.resetConfirmMessage'),
      variant: 'danger',
      confirmText: t('admin.security.reset'),
      cancelText: t('common.actions.cancel'),
    });
    if (!confirmed) {
      return;
    }
    try {
      await resetMutation.mutateAsync();
      notifications.success(t('admin.security.resetSuccess'));
      setHasChanges(false);
    } catch (err: any) {
      notifications.error(t('admin.security.resetError'));
    }
  };

  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl
    transition-all duration-300`;

  const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none text-sm`;

  const labelClasses = 'block text-sm font-medium text-gray-700 mb-2';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner className="w-8 h-8 text-teal-600" />
        <span className="ml-3 text-gray-600">{t('admin.security.loadingMsg')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 rounded-2xl border border-red-200">
        <div className="flex items-center gap-3 text-red-600">
          <AlertTriangle className="w-5 h-5" />
          <span>{t('admin.security.loadingError')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-teal-100 rounded-2xl">
            <Shield className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('admin.security.title')}</h2>
            <p className="text-sm text-gray-500">{t('admin.security.subtitle')}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={handleReset}
            disabled={resetMutation.isPending}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${resetMutation.isPending ? 'animate-spin' : ''}`} />
            {t('admin.security.reset')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
            className="flex items-center gap-2"
          >
            {updateMutation.isPending ? (
              <Spinner className="w-4 h-4" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {t('admin.security.save')}
          </Button>
        </div>
      </div>

      {/* Summary Banner */}
      {summary && (
        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-4 border border-teal-200">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-4 h-4 text-teal-600" />
            <span className="text-sm font-medium text-teal-700">{t('admin.security.summaryTitle')}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            <div className="bg-white/50 rounded-xl p-2">
              <span className="text-gray-500">{t('admin.security.sessions')}</span>
              <span className="ml-1 font-medium text-gray-700">{summary.sessionTimeout}</span>
            </div>
            <div className="bg-white/50 rounded-xl p-2">
              <span className="text-gray-500">{t('admin.security.maxSessions')}</span>
              <span className="ml-1 font-medium text-gray-700">{summary.maxSessions}</span>
            </div>
            <div className="bg-white/50 rounded-xl p-2">
              <span className="text-gray-500">{t('admin.security.twoFA')}</span>
              <span className="ml-1 font-medium text-gray-700">{summary.twoFactorPolicy}</span>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Session Timeout */}
        <div className={cardClasses}>
          <div className="flex items-center gap-3 mb-4">
            <Clock className="w-5 h-5 text-teal-600" />
            <h3 className="font-semibold text-gray-900">{t('admin.security.sessionTimeout')}</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className={labelClasses}>
                {t('admin.security.idleTimeout')}
                <span className="text-xs text-gray-400 ml-2">{t('admin.security.disabled')}</span>
              </label>
              <input
                type="number"
                min="0"
                max="1440"
                value={formData.sessionIdleTimeoutMins || 0}
                onChange={(e) => handleChange('sessionIdleTimeoutMins', parseInt(e.target.value) || 0)}
                className={inputClasses}
              />
            </div>
            <div>
              <label className={labelClasses}>
                {t('admin.security.maxLifetime')}
                <span className="text-xs text-gray-400 ml-2">{t('admin.security.unlimited')}</span>
              </label>
              <input
                type="number"
                min="0"
                max="720"
                value={formData.sessionMaxLifetimeHours || 0}
                onChange={(e) => handleChange('sessionMaxLifetimeHours', parseInt(e.target.value) || 0)}
                className={inputClasses}
              />
            </div>
            <div>
              <label className={labelClasses}>
                {t('admin.security.rememberMe')}
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={formData.rememberMeTimeoutDays || 30}
                onChange={(e) => handleChange('rememberMeTimeoutDays', parseInt(e.target.value) || 30)}
                className={inputClasses}
              />
            </div>
          </div>
        </div>

        {/* Session Controls */}
        <div className={cardClasses}>
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-5 h-5 text-teal-600" />
            <h3 className="font-semibold text-gray-900">{t('admin.security.sessionControls')}</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className={labelClasses}>
                {t('admin.security.concurrentSessions')}
                <span className="text-xs text-gray-400 ml-2">{t('admin.security.unlimitedSessions')}</span>
              </label>
              <input
                type="number"
                min="0"
                max="10"
                value={formData.maxConcurrentSessions || 0}
                onChange={(e) => handleChange('maxConcurrentSessions', parseInt(e.target.value) || 0)}
                className={inputClasses}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-white/30 rounded-xl">
              <div>
                <p className="text-sm font-medium text-gray-700">{t('admin.security.logoutOnIpChange')}</p>
                <p className="text-xs text-gray-500">{t('admin.security.logoutOnIpChangeDesc')}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.forceLogoutOnIpChange || false}
                  onChange={(e) => handleChange('forceLogoutOnIpChange', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
              </label>
            </div>
            <div className="flex items-center justify-between p-3 bg-white/30 rounded-xl">
              <div>
                <p className="text-sm font-medium text-gray-700">{t('admin.security.reauthSensitive')}</p>
                <p className="text-xs text-gray-500">{t('admin.security.reauthSensitiveDesc')}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.requireReauthForSensitive || false}
                  onChange={(e) => handleChange('requireReauthForSensitive', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Password Policy */}
        <div className={cardClasses}>
          <div className="flex items-center gap-3 mb-4">
            <Key className="w-5 h-5 text-teal-600" />
            <h3 className="font-semibold text-gray-900">{t('admin.security.passwordPolicy')}</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className={labelClasses}>{t('admin.security.minLength')}</label>
              <input
                type="number"
                min="8"
                max="128"
                value={formData.passwordMinLength || 12}
                onChange={(e) => handleChange('passwordMinLength', parseInt(e.target.value) || 12)}
                className={inputClasses}
              />
            </div>
            <div>
              <label className={labelClasses}>
                {t('admin.security.passwordHistory')}
                <span className="text-xs text-gray-400 ml-2">{t('admin.security.passwordHistoryHint')}</span>
              </label>
              <input
                type="number"
                min="0"
                max="24"
                value={formData.passwordHistoryCount || 5}
                onChange={(e) => handleChange('passwordHistoryCount', parseInt(e.target.value) || 5)}
                className={inputClasses}
              />
            </div>
            <div>
              <label className={labelClasses}>
                {t('admin.security.expiration')}
                <span className="text-xs text-gray-400 ml-2">{t('admin.security.expirationHint')}</span>
              </label>
              <input
                type="number"
                min="0"
                max="365"
                value={formData.passwordMaxAgeDays || 90}
                onChange={(e) => handleChange('passwordMaxAgeDays', parseInt(e.target.value) || 90)}
                className={inputClasses}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-white/30 rounded-xl">
              <div>
                <p className="text-sm font-medium text-gray-700">{t('admin.security.complexity')}</p>
                <p className="text-xs text-gray-500">{t('admin.security.complexityDesc')}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.passwordRequireComplex || false}
                  onChange={(e) => handleChange('passwordRequireComplex', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Account Lockout */}
        <div className={cardClasses}>
          <div className="flex items-center gap-3 mb-4">
            <Lock className="w-5 h-5 text-teal-600" />
            <h3 className="font-semibold text-gray-900">{t('admin.security.accountLockout')}</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className={labelClasses}>{t('admin.security.lockoutAttempts')}</label>
              <input
                type="number"
                min="3"
                max="10"
                value={formData.accountLockoutAttempts || 5}
                onChange={(e) => handleChange('accountLockoutAttempts', parseInt(e.target.value) || 5)}
                className={inputClasses}
              />
            </div>
            <div>
              <label className={labelClasses}>{t('admin.security.lockoutDuration')}</label>
              <input
                type="number"
                min="5"
                max="1440"
                value={formData.accountLockoutDuration || 30}
                onChange={(e) => handleChange('accountLockoutDuration', parseInt(e.target.value) || 30)}
                className={inputClasses}
              />
            </div>
          </div>
        </div>

        {/* Inactivity Policy */}
        <div className={cardClasses}>
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h3 className="font-semibold text-gray-900">{t('admin.security.inactivityPolicy')}</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className={labelClasses}>
                {t('admin.security.warningAfter')}
                <span className="text-xs text-gray-400 ml-2">ISO 27001 A.9.2.6</span>
              </label>
              <input
                type="number"
                min="30"
                max="180"
                value={formData.inactivityWarningDays || 75}
                onChange={(e) => handleChange('inactivityWarningDays', parseInt(e.target.value) || 75)}
                className={inputClasses}
              />
            </div>
            <div>
              <label className={labelClasses}>
                {t('admin.security.revocationAfter')}
              </label>
              <input
                type="number"
                min="45"
                max="365"
                value={formData.inactivityRevocationDays || 90}
                onChange={(e) => handleChange('inactivityRevocationDays', parseInt(e.target.value) || 90)}
                className={inputClasses}
              />
            </div>
            <p className="text-xs text-gray-500 italic">
              {t('admin.security.inactivityNote')}
            </p>
          </div>
        </div>

        {/* 2FA Settings */}
        <div className={cardClasses}>
          <div className="flex items-center gap-3 mb-4">
            <Smartphone className="w-5 h-5 text-teal-600" />
            <h3 className="font-semibold text-gray-900">{t('admin.security.twoFATitle')}</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-white/30 rounded-xl">
              <div>
                <p className="text-sm font-medium text-gray-700">{t('admin.security.twoFAAll')}</p>
                <p className="text-xs text-gray-500">{t('admin.security.twoFAAllDesc')}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.require2FA || false}
                  onChange={(e) => handleChange('require2FA', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
              </label>
            </div>
            <div className="flex items-center justify-between p-3 bg-white/30 rounded-xl">
              <div>
                <p className="text-sm font-medium text-gray-700">{t('admin.security.twoFAAdmins')}</p>
                <p className="text-xs text-gray-500">{t('admin.security.twoFAAdminsDesc')}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.require2FAForAdmins || false}
                  onChange={(e) => handleChange('require2FAForAdmins', e.target.checked)}
                  disabled={formData.require2FA}
                  className="sr-only peer"
                />
                <div className={`w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600 ${formData.require2FA ? 'opacity-50' : ''}`}></div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* IP Whitelist Settings - ISO 27001 A.9.4.2 */}
      <div className={cardClasses}>
        <IpWhitelistSettings />
      </div>

      {/* Last Modified Info */}
      {settings?.updatedAt && (
        <div className="text-xs text-gray-400 text-right">
          {t('admin.security.lastModified')} {new Date(settings.updatedAt).toLocaleString('fr-FR')}
          {settings.lastModifiedBy && ` ${t('admin.security.by')} ${settings.lastModifiedBy}`}
        </div>
      )}
    </div>
  );
};

export default SecuritySettings;
